/**
 * Transcript Tab Component for Meeting Detail
 * Displays and manages meeting transcripts with speaker diarization
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, Edit3, Save, X, RefreshCw, Users, Search,
  Loader2, CheckCircle, Clock, Download, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TranscriptTab = ({
  meeting,
  token,
  transcript,
  onTranscriptUpdate
}) => {
  // Use full_text as fallback if text is empty (API may return full_text instead of text)
  const transcriptText = transcript?.text || transcript?.full_text || '';
  
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [editedText, setEditedText] = useState(transcriptText);
  const [saving, setSaving] = useState(false);
  const [diarizing, setDiarizing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSaveTranscript = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/meeting-intelligence/meetings/${meeting.id}/transcript?token=${token}`,
        { text: editedText }
      );
      toast.success('Transcript saved');
      setEditingTranscript(false);
      if (onTranscriptUpdate) onTranscriptUpdate({ ...transcript, text: editedText, full_text: editedText });
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save transcript');
    } finally {
      setSaving(false);
    }
  };

  const handleDiarize = async () => {
    try {
      setDiarizing(true);
      const response = await axios.post(
        `${API}/api/meeting-intelligence/meetings/${meeting.id}/diarize?token=${token}`
      );
      toast.success('Speaker identification complete');
      if (onTranscriptUpdate) onTranscriptUpdate(response.data.transcript);
    } catch (err) {
      console.error('Diarization failed:', err);
      toast.error(err.response?.data?.detail || 'Speaker identification failed');
    } finally {
      setDiarizing(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(transcriptText);
    toast.success('Transcript copied to clipboard');
  };

  const exportTranscript = () => {
    const content = transcript?.diarized_segments?.length > 0
      ? transcript.diarized_segments.map(s => `[${s.speaker}] ${s.text}`).join('\n\n')
      : transcriptText;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${meeting.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Transcript downloaded');
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter segments by search
  const filteredSegments = transcript?.diarized_segments?.filter(seg =>
    !searchQuery || seg.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasTranscript = transcriptText.length > 0;
  const hasDiarization = transcript?.diarized_segments?.length > 0;

  return (
    <Card data-testid="transcript-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Transcript
            </CardTitle>
            <CardDescription>
              {hasTranscript 
                ? `${transcriptText.split(' ').length} words • ${formatTimestamp(transcript.duration || 0)} duration`
                : 'No transcript available'}
            </CardDescription>
          </div>
          
          {hasTranscript && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={exportTranscript}>
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              {!hasDiarization && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDiarize}
                  disabled={diarizing}
                >
                  {diarizing ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Users className="h-4 w-4 mr-1" />
                  )}
                  Identify Speakers
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {!hasTranscript ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No transcript available</p>
            <p className="text-sm">Upload a recording to generate transcript</p>
          </div>
        ) : (
          <>
            {/* Search */}
            {hasDiarization && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search transcript..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="transcript-search"
                />
              </div>
            )}
            
            {/* Diarized View */}
            {hasDiarization ? (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {(searchQuery ? filteredSegments : transcript.diarized_segments).map((segment, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      searchQuery && segment.text.toLowerCase().includes(searchQuery.toLowerCase())
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200'
                        : 'bg-slate-50 dark:bg-slate-800/50'
                    }`}
                    data-testid={`transcript-segment-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="secondary" className="text-xs">
                        {segment.speaker || `Speaker ${idx + 1}`}
                      </Badge>
                      <span className="text-xs text-slate-400">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {formatTimestamp(segment.start || 0)} - {formatTimestamp(segment.end || 0)}
                      </span>
                    </div>
                    <p className="text-sm">{segment.text}</p>
                  </div>
                ))}
                
                {searchQuery && filteredSegments?.length === 0 && (
                  <div className="text-center py-4 text-slate-500">
                    No matches found for "{searchQuery}"
                  </div>
                )}
              </div>
            ) : (
              /* Plain Text View */
              <div className="relative">
                {editingTranscript ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      rows={15}
                      className="font-mono text-sm"
                      data-testid="transcript-editor"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleSaveTranscript} disabled={saving}>
                        {saving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Save
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setEditingTranscript(false);
                          setEditedText(transcriptText);
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="max-h-[400px] overflow-y-auto p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <p className="whitespace-pre-wrap text-sm">{transcriptText}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setEditingTranscript(true)}
                    >
                      <Edit3 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-slate-500 pt-2 border-t">
              <span>{transcriptText.split(' ').length} words</span>
              {hasDiarization && (
                <span>{new Set(transcript.diarized_segments.map(s => s.speaker)).size} speakers</span>
              )}
              <span>{transcript.segments?.length || 0} segments</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TranscriptTab;
